// src/app/features/warehouse/store/warehouse.state.ts

import {
  PaginatedResponse,
  InvoiceListItem,
  InvoiceDetail,
  Article,
  ArticleType,
  StockItem,
  StockMovementResponse,
  KomitentListItem,
  KomitentDetail,
  WarehouseSummary,
} from '../models/warehouse.model';

// ── Cache entry ─────────────────────────────────────────────────────────────
export interface CacheEntry<T> {
  data: T;
  loadedAt: number;   // Date.now()
  ttlMs: number;
}

export function isCacheValid<T>(entry: CacheEntry<T> | null | undefined): boolean {
  if (!entry) return false;
  return Date.now() - entry.loadedAt < entry.ttlMs;
}

// ── TTL constants (ms) ──────────────────────────────────────────────────────
export const TTL = {
  STATIC:     30 * 60 * 1000,   // 30 min  — article types, summary
  LIST:        2 * 60 * 1000,   // 2 min   — paginated lists (invoices, articles, stock, komitents)
  DETAIL:      5 * 60 * 1000,   // 5 min   — single-item detail
  SEARCH:      2 * 60 * 1000,   // 2 min   — person/komitent search
} as const;

// ── Async slice ─────────────────────────────────────────────────────────────
export interface AsyncSlice<T> {
  data: T | null;
  loading: boolean;
  loaded: boolean;
  error: string | null;
  cache: CacheEntry<T> | null;
}

export function emptySlice<T>(): AsyncSlice<T> {
  return { data: null, loading: false, loaded: false, error: null, cache: null };
}

// ── State shape ─────────────────────────────────────────────────────────────

export interface WarehouseState {
  // Paginated lists
  invoices:      AsyncSlice<PaginatedResponse<InvoiceListItem>>;
  articles:      AsyncSlice<PaginatedResponse<Article>>;
  articleTypes:  AsyncSlice<PaginatedResponse<ArticleType>>;
  stock:         AsyncSlice<PaginatedResponse<StockItem>>;
  lowStock:      AsyncSlice<PaginatedResponse<StockItem>>;
  komitents:     AsyncSlice<PaginatedResponse<KomitentListItem>>;

  // Keyed detail records
  /** keyed by invoice id */
  invoiceDetail:  Record<number, AsyncSlice<InvoiceDetail>>;
  /** keyed by komitent id */
  komitentDetail: Record<number, AsyncSlice<KomitentDetail>>;
  /** keyed by artId */
  stockMovements: Record<number, AsyncSlice<StockMovementResponse>>;

  // Dashboard summary
  summary: AsyncSlice<WarehouseSummary>;
}

// ── Initial state ───────────────────────────────────────────────────────────
export const initialWarehouseState: WarehouseState = {
  invoices:       emptySlice(),
  articles:       emptySlice(),
  articleTypes:   emptySlice(),
  stock:          emptySlice(),
  lowStock:       emptySlice(),
  komitents:      emptySlice(),
  invoiceDetail:  {},
  komitentDetail: {},
  stockMovements: {},
  summary:        emptySlice(),
};
